// 🔒 JORNADA-CERRADA-2026-10-26 — Pon Pan: después de cerrar el día, volver a entrar abría solo otra jornada
// con la misma fecha y las ventas del día siguiente se sumaban. Ahora, si hoy ya se cerró, se pregunta:
// entrar sin abrir (reportes, contabilidad, configuración) o reabrir con una segunda confirmación.
import { useState } from 'react';
import { Lock, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { businessDaysAPI } from '@/lib/api';
import { notify } from '@/lib/notify';
import { fechaCorta, fechaLarga, hoyRD } from '@/lib/jornadaCerrada';

export default function JornadaCerradaDialog({ claro, onReabierta, onSalir }) {
  const { jornadaCerrada: jc, setJornadaCerrada, hasPermission } = useAuth();
  const [paso, setPaso] = useState('elegir');
  const [ocupado, setOcupado] = useState(false);
  if (!jc) return null;

  const puedeReabrir = hasPermission('close_day');  // mismo permiso que cerrar el día (Leo: opción A)
  const texto = claro ? { color: '#1E293B', WebkitTextFillColor: '#1E293B' } : { color: 'white' };
  const suave = claro ? { color: '#475569', WebkitTextFillColor: '#475569' } : { color: 'rgba(255,255,255,0.75)' };
  const caja = claro
    ? { backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }
    : { backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' };

  const terminar = () => { setPaso('elegir'); setJornadaCerrada(null); };

  const reabrir = async () => {
    // Si ya pasó la medianoche, reabrir esa fecha juntaría las ventas del día nuevo: no se hace.
    if (hoyRD() !== jc.fecha) {
      notify.info('Ya es un día nuevo', { description: 'Sal y vuelve a entrar para abrir la jornada de hoy.' });
      terminar();
      return;
    }
    setOcupado(true);
    try {
      await businessDaysAPI.open({ business_date: jc.fecha, authorizer_pin: 'self', confirmar_reabrir: true });
      notify.success(`Jornada del ${fechaCorta(jc.fecha)} reabierta`, {
        description: 'Las ventas se juntan con las de esa jornada.',
      });
      terminar();
      if (onReabierta) onReabierta();
    } catch (e) {
      notify.error('No se pudo reabrir la jornada', { description: e.response?.data?.detail || e.message });
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        className={`max-w-md ${claro ? 'bg-white border-amber-500/50' : 'bg-slate-900 border-amber-500/50'}`}
        hideCloseButton
        data-testid="jornada-cerrada-dialog"
      >
        {paso === 'elegir' ? (
          <>
            <DialogHeader>
              <div className="flex justify-center mb-3">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${claro ? 'bg-amber-100' : 'bg-amber-500/20'}`}>
                  <Lock className="w-7 h-7 text-amber-500" />
                </div>
              </div>
              <DialogTitle className="text-xl font-oswald text-center" style={texto}>
                La jornada de hoy ya se cerró
              </DialogTitle>
              <DialogDescription className="text-center" style={suave}>
                {puedeReabrir ? '¿Qué quieres hacer?' : 'No se puede vender hasta que un gerente abra la jornada de mañana.'}
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg p-3 text-sm" style={{ ...caja, ...texto }}>
              <div>Fecha: <b>{fechaLarga(jc.fecha)}</b></div>
              <div>Cerró: <b>{jc.cerrada_por || '—'}</b>{jc.hora_cierre ? ` · ${jc.hora_cierre}` : ''}</div>
            </div>
            <div className="flex flex-col gap-2 mt-2">
              <Button onClick={terminar} className="h-auto py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold"
                data-testid="jornada-entrar-sin-abrir">
                <span className="flex flex-col">
                  <span>Entrar sin abrir</span>
                  <span className="text-xs font-normal">Reportes, contabilidad, inventario, configuración</span>
                </span>
              </Button>
              {puedeReabrir && (
                <Button variant="outline" onClick={() => setPaso('confirmar')}
                  className="h-12 font-bold border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100"
                  data-testid="jornada-reabrir">
                  Reabrir jornada del {fechaCorta(jc.fecha).slice(0, 5)}
                </Button>
              )}
              <Button variant="outline" onClick={() => { setJornadaCerrada(null); if (onSalir) onSalir(); }}
                className="h-12 font-bold" data-testid="jornada-salir">
                Salir
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex justify-center mb-3">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${claro ? 'bg-red-100' : 'bg-red-500/20'}`}>
                  <AlertTriangle className="w-7 h-7 text-red-500" />
                </div>
              </div>
              <DialogTitle className="text-xl font-oswald text-center" style={texto}>
                ¿Seguro que quieres reabrir?
              </DialogTitle>
              <DialogDescription className="text-center" style={suave}>
                Las ventas que hagas ahora <b>se van a juntar</b> con las de la jornada del <b>{fechaCorta(jc.fecha)}</b> que
                ya se cerró. El reporte del día saldrá sumado.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 mt-2">
              <Button onClick={reabrir} disabled={ocupado} className="h-12 bg-red-600 hover:bg-red-700 text-white font-bold"
                data-testid="jornada-confirmar-reabrir">
                {ocupado ? 'Reabriendo…' : 'Sí, reabrir y juntar ventas'}
              </Button>
              <Button variant="outline" onClick={() => setPaso('elegir')} disabled={ocupado} className="h-12 font-bold"
                data-testid="jornada-volver">
                No, volver atrás
              </Button>
            </div>
            <p className="text-xs text-center mt-1" style={suave}>Queda en la Bitácora quién reabrió y a qué hora.</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
